import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { TrainingMembership } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

import { useCertificateDownload } from './useCertificateDownload'

export const ExportCertificates = ({
  isOpen,
  onClose,
  trainingMemberships,
  selectedParticipantIds,
}: {
  isOpen: boolean
  onClose: () => void
  trainingMemberships: TrainingMembership[]
  selectedParticipantIds: number[]
}) => {
  const allParticipantEmployeeIds = useMemo(
    () =>
      trainingMemberships
        .map((membership) => membership.employee?.id)
        .filter((id): id is number => id !== undefined),
    [trainingMemberships]
  )

  const [selection, setSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: selectedParticipantIds,
  })

  const { downloadCertificates, isDownloading, hasNoCertificates, hasError, resetStatus } =
    useCertificateDownload(trainingMemberships)

  useEffect(() => {
    setSelection({
      type: 'manual',
      ids: selectedParticipantIds,
    })
    resetStatus()
  }, [selectedParticipantIds, resetStatus])

  const handleExport = useCallback(async () => {
    let idsToDownload: number[]

    if (selection.type === 'manual') {
      idsToDownload = selection.ids
    } else {
      const excludedIds = selection.excludedIds || []
      idsToDownload = allParticipantEmployeeIds.filter((id) => !excludedIds.includes(id))
    }

    await downloadCertificates(idsToDownload)
  }, [selection, allParticipantEmployeeIds, downloadCertificates])

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.export_certificates'
      )}
      primaryAction={{
        label: i18n.t('common.export'),
        onClick: handleExport,
        disabled: isDownloading,
        loading: isDownloading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s32">
        <EmployeeSelectorV2
          multiple
          value={selectedParticipantIds}
          onChange={setSelection}
          forcedFilters={{
            employeeIds: allParticipantEmployeeIds,
          }}
          label={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.select_participants'
          )}
          mandatory
        />
        {hasNoCertificates && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.no_certificates_to_download'
            )}
            variant="warning"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.no_certificates_to_download_description'
            )}
          />
        )}
        {hasError && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.export_certificates_error'
            )}
            variant="critical"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.export_certificates_error_description'
            )}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
